<template>
  <div>
    <div v-if="configuration.type !== 'range'">
      <vue-fuse
        :keys="['name']"
        :list="options"
        class="search"
        placeholder="Search"
        @fuseResultsUpdated="search"
      />
    </div>
    <div
      v-if="configuration.excludable && configuration.type !== 'range'"
      class="exclude pt-2 pb-2 pl-3 pr-3 border-bottom"
    >
      <b-form-radio-group
        v-model="isExclude"
        :options="[
          { text: 'Exclude', value: true },
          { text: 'Include', value: false }
        ]"
      />
    </div>
    <div>
      <slot :configuration="configuration">
        <div class="body p-3">
          <b-form-group
            v-if="configuration.type === 'checkbox'"
            :label="configuration.label"
            class="mb-0"
          >
            <transition name="fade" mode="out-in">
              <tree
                v-if="showOptions.length"
                :options="showOptions"
                v-model="selected"
              >
                <span slot="checkbox-title" slot-scope="{ node }">
                  {{ node.name }}
                  <span class="text-light small ml-1">
                    ({{ node.count | formatWideInt }})
                  </span>
                </span>
              </tree>
              <p v-else-if="!isLoading" class="mb-0">
                {{ $t('noResultsFound') }}
              </p>
              <div v-else class="text-center">
                <b-spinner class="m-auto" variant="primary" type="grow" />
              </div>
            </transition>
          </b-form-group>
          <b-form-row v-if="configuration.type === 'range'" class="d-flex">
            <b-col>
              <b-form-group class="mb-0" :label="`${$t('min')}:` | capitalize">
                <b-form-input
                  type="text"
                  size="lg"
                  v-model.number="range.min"
                />
              </b-form-group>
            </b-col>
            <b-col>
              <b-form-group class="mb-0" :label="`${$t('max')}:` | capitalize">
                <b-form-input
                  type="text"
                  size="lg"
                  v-model.number="range.max"
                />
              </b-form-group>
            </b-col>
          </b-form-row>
        </div>
      </slot>
    </div>
    <div class="footer border-top">
      <slot name="footer">
        <div class="d-flex align-items-end flex-column pl-3 pb-2 pr-3 pt-2">
          <b-button
            @click="submit()"
            class="btn"
            variant="primary"
            size="sm"
            :disabled="disabled"
          >
            {{ $t('submit') }}
          </b-button>
        </div>
      </slot>
    </div>
  </div>
</template>

<script>
import cloneDeep from 'clone';
import Tree from '@/components/TreeView/Tree.vue';
let cancel;

export default {
  components: { Tree },
  props: {
    configuration: {
      type: Object,
      required: true
    },
    apiName: {
      type: String,
      default: 'adnet'
    }
  },

  data() {
    return {
      options: [],
      showOptions: [],
      selected: [],
      isExclude: false,
      isLoading: false,
      searchText: null,
      range: {
        min: null,
        max: null
      }
    };
  },

  computed: {
    disabled() {
      if (this.range.min || this.range.max) return false;
      if (Array.isArray(this.selected)) {
        return this.selected.length === 0;
      }
      if (typeof this.selected === 'object' && this.selected !== null) {
        return Object.keys(this.selected).length === 0;
      }

      return !this.selected;
    }
  },

  methods: {
    async init() {
      if (typeof this.configuration.options === 'string') {
        this.cancelRequest();
        this.isLoading = true;
        const api = this.configuration.api || 'adnet';
        const { values } = await this.$api[api].get(
          this.configuration.options,
          {
            cancelToken: new this.$api.client.CancelToken(function executor(c) {
              cancel = c;
            })
          }
        );
        this.options = values;
        this.isLoading = false;
      } else {
        this.options = cloneDeep(this.configuration.options);
      }
      if (this.configuration.type === 'range') {
        const value = this.configuration.value || {};
        this.range.min = value.min || null;
        this.range.max = value.max || null;
      } else {
        this.selected = this.configuration.value || [];
      }
    },
    cancelRequest() {
      if (typeof cancel !== 'function') return;
      cancel();
      this.isLoading = false;
    },
    submit() {
      const emitValueTemplate = {
        exclude: this.isExclude,
        configurationName: this.configuration.name,
        key: this.configuration.key
      };

      const getValue = () => {
        if (this.configuration.type === 'range') {
          return {
            ...emitValueTemplate,
            value: this.range,
            type: this.configuration.type
          };
        }
        return {
          ...emitValueTemplate,
          value: Array.isArray(this.selected) ? this.selected : [this.selected],
          type: this.configuration.type,
          condition: this.isExclude ? '!=' : '='
        };
      };

      this.$emit('submit', getValue());
    },
    search(options) {
      this.showOptions = options.map(item => {
        if (item.name instanceof Object) {
          item.name = item.name.title;
        }
        return item;
      });
    }
  },

  created() {
    this.init();
  },

  beforeDestroy() {
    this.cancelRequest();
  },

  watch: {
    configuration: {
      deep: true,
      handler() {
        this.init();
      }
    }
  }
};
</script>

<style lang="scss">
.search {
  display: block;
  width: 100%;
  border: none;
  border-bottom: 1px solid $border-color;
  padding: 0.5rem 1rem;
}

.body {
  max-height: 160px;
  box-sizing: border-box;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}
</style>
